import { useEffect, useMemo, useRef, useState } from 'react';

import { useDebouncedValue } from '@src/packages/utils';
import type { ExecutionsHistorySectionResult } from '@src/models/interfaces/executionsHistoryDashboard.interface';
import ExecutionsHistoryDashboardService from '@src/services/ExecutionsHistoryDashboardService';

const PAGE_SIZE = 25;
const SEARCH_DEBOUNCE_MS = 300;

export interface HistoryDrilldownConfig<TQuery, TTarget, TData> {
  /** Calls this report's one drilldown endpoint (Schedules, Routes, ...). */
  loadDrilldown: (
    service: ExecutionsHistoryDashboardService,
    request: TQuery & { drilldown: TTarget; page: number; pageSize: number; search: string },
  ) => Promise<ExecutionsHistorySectionResult<TData>>;
}

/**
 * Loads one page of drilldown rows behind a report chart, with search debounce and
 * stale-response discarding. Shared by every executions-history report; entity-specific
 * wrappers only supply the service call (see `useScheduleHistoryDrilldown` / `useRouteHistoryDrilldown`).
 */
export const useHistoryDrilldown = <TQuery, TTarget, TData extends { total: number }>(
  query: TQuery | undefined,
  target: TTarget | null,
  search: string,
  page: number,
  { loadDrilldown }: HistoryDrilldownConfig<TQuery, TTarget, TData>,
) => {
  const serviceRef = useRef<ExecutionsHistoryDashboardService | null>(null);
  if (!serviceRef.current) serviceRef.current = new ExecutionsHistoryDashboardService();
  const generationRef = useRef(0);
  const [retryCount, setRetryCount] = useState(0);
  const [state, setState] = useState<{
    loading: boolean;
    result?: ExecutionsHistorySectionResult<TData>;
  }>({ loading: false });
  const debouncedSearch = useDebouncedValue(search, SEARCH_DEBOUNCE_MS);
  const request = useMemo(
    () =>
      query && target
        ? { ...query, drilldown: target, page, pageSize: PAGE_SIZE, search: debouncedSearch.trim() }
        : null,
    [debouncedSearch, page, query, target],
  );
  const requestKey = request ? JSON.stringify({ request, retryCount }) : null;

  useEffect(() => {
    generationRef.current += 1;
    if (!request) {
      setState({ loading: false });
      return;
    }
    const generation = generationRef.current;
    setState({ loading: true });
    const load = async () => {
      const result = await loadDrilldown(serviceRef.current!, request);
      if (generation === generationRef.current) setState({ loading: false, result });
    };
    void load();
    // requestKey captures query, target, page and debounced search content.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestKey]);

  const data = state.result?.status === 'ok' ? state.result.response.data : undefined;
  return {
    loading: state.loading,
    result: state.result,
    data,
    total: data?.total ?? 0,
    pageSize: PAGE_SIZE,
    retry: () => setRetryCount(count => count + 1),
  };
};
